import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { useMediaErrorRefresh } from '@/hooks/useMediaErrorRefresh';
import { usePermissions, useWorkspace } from '@/contexts/WorkspaceContext';
import { PreviewRecipient, type SendRequirements } from '@/lib/whatsappPreview';
import { useLaunchConfigAutosave } from '@/hooks/useLaunchConfigAutosave';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { unmappedVariablesOf, needsMappingReview, type MappingReview } from '@/lib/templateVariables';
import { channelOfCampaign } from '@/lib/campaignChannel';
import { LEAD_STATUS_NONE } from '@/lib/contactFields';

// Everything a campaign draft is set up to send, and the launch itself.
//
// This was the body of CampaignLaunchEditor. It is a hook now because the setup wizard spreads that
// one editor across three steps (channel, contacts, template & launch) with a preview beside them,
// and the state CANNOT be split along those lines: `launchConfig` is autosaved wholesale, the credit
// quote depends on both the channel and the audience, and the header media depends on the template.
// So one owner holds it, stays mounted while the operator moves between steps, and the step
// components only render what it returns.

// Radix Select reserves the empty string for "no selection", and throws if an item uses it.
export const ALL_SOURCES = '__all__';

// Server-computed credit quote. The rate card is per-plan and lives on the backend
// (config/messageCosts.js + Plan), so the dialog renders what it is told rather than
// mirroring a rate table that would drift.
export type CostKind = 'whatsapp_init' | 'whatsapp_next' | 'sms';
// `segments` is on the SMS line: the provider bills SMS per segment, so the rate is multiplied by
// how many segments the body is (backend utils/smsSegments.js).
export type CostLine = { count: number; rate: number; credits: number; segments?: number };
export type CostBreakdown = Record<CostKind, CostLine>;
export type SmsEncoding = { segments: number; encoding: 'GSM-7' | 'UCS-2' };

export const COST_LABELS: Record<CostKind, string> = {
  whatsapp_init: 'WhatsApp, first message',
  whatsapp_next: 'WhatsApp, follow-up',
  sms: 'SMS',
};

export interface WhatsAppTemplateOption {
  id: string;
  name: string;
  language: string;
  category: string;
  body: string;
  headerType?: string | null;
  variableMapping: Record<string, string>;
  metadata?: {
    variableLabels?: Record<string, string>;
    mappingReview?: MappingReview | null;
  } | null;
  /**
   * The generated media the template was made from, and the photo or video it was reviewed with —
   * what a campaign using it starts with. Null for a template not made from media.
   */
  campaignMediaId?: string | null;
  headerAssetUid?: string | null;
}

// One of the campaign's own generated images or videos, offered as the header media. This is the
// whole point of resolving media server-side: the picture was made by this product and is already
// in storage, so nobody should have to download it and upload it back.
export interface CampaignAsset {
  uid: string;
  kind: 'photo' | 'video';
  label: string;
  mime: string | null;
  selected: boolean;
  previewUrl: string | null;
}

const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

// What WhatsApp accepts for each header format. Narrowing the picker saves a round trip and an
// error nobody enjoys reading.
export const MEDIA_ACCEPT: Record<string, string> = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};

// Sentinel for "upload or paste instead of using a campaign asset" — Radix Select throws on an
// empty-string item value.
export const CUSTOM_MEDIA = '__custom__';

// Every key here must also be accepted by audienceFiltersSchema (backend
// controllers/campaignLaunch/_shared.js) and launchConfigSchema (controllers/campaign/_shared.js):
// the launch refuses an unknown filter, and the draft autosave fails without saying so. '' = any.
export type FilterState = {
  search: string;
  source: string;
  propertyInterest: string;
  locationPreference: string;
  leadStatus: string;
  leadCampaign: string;
  // ISO alpha-2 code.
  currentCountry: string;
};

export type AudienceMode = 'selected' | 'filtered' | 'all';
export type { Channel } from '@/lib/campaignChannel';

export interface LaunchableCampaign {
  id: string;
  name: string;
  type: string;
  campaignMediaId?: string | null;
  /** What was configured last time, restored on mount. See useLaunchConfigAutosave. */
  launchConfig?: {
    channel?: string;
    whatsappTemplateId?: string | null;
    audience?: { mode?: string; contactIds?: string[]; filters?: Partial<FilterState> };
    message?: string;
    headerMedia?: { assetUid?: string; url?: string; mediaId?: string } | null;
    headerText?: string;
    buttonValues?: Record<string, string>;
  } | null;
  /** The creative, for the SMS body. WhatsApp sends the approved template instead. */
  media?: {
    id: string;
    name: string;
    generatedContent?: { text?: string };
  } | null;
}

interface UseCampaignLaunchStateOptions {
  campaign: LaunchableCampaign | null;
  workspaceId: string;
  /** Called with the launched campaign so the page can swap to results in place. */
  onLaunched: (campaign: { id: string; status: string }) => void;
  /** With the refetched campaign after its media changed — see switchCampaignMedia. */
  onCampaignChange?: (campaign: LaunchableCampaign) => void;
}

export function useCampaignLaunchState({
  campaign, workspaceId, onLaunched, onCampaignChange,
}: UseCampaignLaunchStateOptions) {
  const showError = useApiErrorToast();
  const { can } = usePermissions();
  const { refreshWorkspace } = useWorkspace();
  // Mirrors the /sync-all route gate: template authors, and launchers who need a current list.
  const canSyncTemplates = can('templates:manage') || can('campaign:launch');
  const [mode, setMode] = useState<AudienceMode>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    source: '',
    propertyInterest: '',
    locationPreference: '',
    leadStatus: '',
    leadCampaign: '',
    currentCountry: '',
  });
  // Fixed by the campaign, not chosen here: the tab it was created from decided it (see
  // lib/campaignChannel). Derived rather than held, so there is no state for anything to drift.
  const channel = channelOfCampaign(campaign);
  const [scheduled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [launching, setLaunching] = useState(false);
  const [previewCount, setPreviewCount] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState(0);
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown | null>(null);
  const [smsEncoding, setSmsEncoding] = useState<SmsEncoding | null>(null);
  const [totalContacts, setTotalContacts] = useState(0);
  const [whatsappTemplateId, setWhatsappTemplateId] = useState('');

  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplateOption[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // A failed template fetch is NOT an empty template list. Conflating them rendered the amber
  // "None are available - press Sync templates" panel when the request had simply failed, which
  // sends somebody off to run a sync that cannot help.
  const [templatesError, setTemplatesError] = useState(false);
  const [syncingTemplates, setSyncingTemplates] = useState(false);
  // Why the last sync could not reach the provider. The list on screen is still the last synced one.
  const [templatesSyncIssue, setTemplatesSyncIssue] = useState<string | null>(null);
  // Bumped after every sync so anything derived from a template's detail refetches, even when the
  // selected id itself did not change.
  const [templatesRevision, setTemplatesRevision] = useState(0);

  // What the chosen template needs, and what the campaign is supplying for it. Tagged with the
  // template it was fetched for: between picking a template and its details arriving, the PREVIOUS
  // template's answer must not stand in for it — that is how a VIDEO template used to fetch IMAGE
  // options and keep one.
  const [fetchedRequirements, setFetchedRequirements] = useState<{
    templateId: string; data: SendRequirements;
  } | null>(null);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [campaignAssets, setCampaignAssets] = useState<CampaignAsset[]>([]);
  const [loadingHeaderAssets, setLoadingHeaderAssets] = useState(false);
  // Bumped to fetch the header assets again when a preview fails to load — its signed URL expired on
  // a page left open. That refetch is quiet: nothing about the choice changed, so Launch must not
  // flicker to "Loading the header media…", and a failure keeps the assets already on screen.
  const [headerAssetsRefresh, setHeaderAssetsRefresh] = useState(0);
  const quietHeaderRefresh = useRef(false);
  // Throttled here, once, because the step's thumbnail and the preview both show these assets.
  const refreshHeaderPreviews = useMediaErrorRefresh(() => {
    quietHeaderRefresh.current = true;
    setHeaderAssetsRefresh((n) => n + 1);
  });
  // Which of `campaignAssets` is the chosen template's own default — for the badge saying so.
  const [templateDefaultUid, setTemplateDefaultUid] = useState<string | null>(null);
  const [mediaChoice, setMediaChoice] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [headerMediaId, setHeaderMediaId] = useState('');
  const [uploadedMediaName, setUploadedMediaName] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [headerText, setHeaderText] = useState('');
  const [buttonValues, setButtonValues] = useState<Record<string, string>>({});

  // A handful of real contacts to resolve the preview's {{n}} against. A sample, not a picker — the
  // audience step is what decides who actually receives this.
  const [previewRecipients, setPreviewRecipients] = useState<PreviewRecipient[]>([]);
  const [previewRecipientId, setPreviewRecipientId] = useState('');

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<{ contacts: PreviewRecipient[] }>(
          `/workspaces/${workspaceId}/contacts?limit=10`,
        );
        if (cancelled) return;
        setPreviewRecipients(data.contacts || []);
        setPreviewRecipientId((prev) => prev || data.contacts?.[0]?.id || '');
      } catch (error) {
        // A preview without a real contact still renders, as annotated placeholders.
        console.warn('[Campaign launch] preview recipients fetch failed:', error);
        if (!cancelled) setPreviewRecipients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  const previewRecipient = previewRecipients.find((c) => c.id === previewRecipientId) || null;

  const { save: saveConfig, flush: flushConfig } = useLaunchConfigAutosave(
    workspaceId,
    campaign?.id,
    Boolean(campaign),
  );

  // Restore what was configured last time. Guarded on the campaign id rather than on the config
  // object, so a later autosave round-trip cannot re-hydrate over something being typed.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    const config = campaign?.launchConfig;
    if (!campaign || hydratedFor.current === campaign.id) return;
    hydratedFor.current = campaign.id;

    // The channel is not restored: `config.channel` is only ever a copy of the campaign's type now,
    // and the type is what `channel` above already reads.
    if (config?.whatsappTemplateId) setWhatsappTemplateId(String(config.whatsappTemplateId));
    if (config?.message) setCustomMessage(String(config.message));
    if (config?.headerText) setHeaderText(String(config.headerText));
    if (config?.buttonValues) setButtonValues(config.buttonValues as Record<string, string>);
    if (config?.headerMedia?.assetUid) setMediaChoice(String(config.headerMedia.assetUid));
    else if (config?.headerMedia?.url) {
      setMediaChoice(CUSTOM_MEDIA);
      setHeaderMediaUrl(String(config.headerMedia.url));
    }

    const audience = config?.audience;
    if (audience?.mode) setMode(audience.mode as AudienceMode);
    if (audience?.contactIds) setSelectedIds(audience.contactIds as string[]);
    if (audience?.filters) {
      // 'new' is what "not contacted yet" was called before lead status became campaign-driven.
      const { leadStatus } = audience.filters;
      setFilters((prev) => ({
        ...prev,
        ...audience.filters,
        ...(leadStatus === 'new' ? { leadStatus: LEAD_STATUS_NONE } : {}),
      }));
    }
  }, [campaign]);

  // Persist the whole configuration whenever any part of it changes. Replaced wholesale rather than
  // merged, which is what makes clearing a field possible at all.
  useEffect(() => {
    if (!campaign || hydratedFor.current !== campaign.id) return;

    const headerMedia = headerMediaId
      ? { mediaId: headerMediaId }
      : mediaChoice === CUSTOM_MEDIA && headerMediaUrl
        ? { url: headerMediaUrl }
        : mediaChoice && mediaChoice !== CUSTOM_MEDIA
          ? { assetUid: mediaChoice }
          : null;

    saveConfig({
      channel,
      whatsappTemplateId: whatsappTemplateId || null,
      audience: { mode, contactIds: selectedIds, filters },
      message: customMessage,
      headerMedia,
      headerText,
      buttonValues,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, whatsappTemplateId, mode, selectedIds, filters, customMessage,
      mediaChoice, headerMediaUrl, headerMediaId, headerText, buttonValues]);

  // Fetch preview count + credit quote when mode, filters or channel change.
  // Debounced: `filters` is a new object on every keystroke, so without this each character
  // typed fired its own request.
  //
  // `channel` is in the body and the deps because pricing is channel-aware — a WhatsApp
  // send costs 7.5 credits the first time it reaches a contact and 5.4 after, while SMS is
  // 0.32 — so toggling the channel must re-quote.
  //
  // So is the message, for SMS: it is priced per segment, and the segments are the body's. The
  // server resolves the body the launch would send (this message, else the media's text), so the
  // quote is the charge.
  useEffect(() => {
    if (!workspaceId || !campaign) return;

    const fetchPreview = async () => {
      setLoadingPreview(true);
      try {
        const body: Record<string, unknown> = { mode, channel };
        if (mode === 'selected') body.contactIds = selectedIds;
        if (mode === 'filtered') body.filters = filters;
        if (channel !== 'whatsapp' && customMessage.trim()) body.message = customMessage;

        const response = await apiFetch<{
          count: number;
          estimatedCredits: number;
          breakdown: CostBreakdown;
          sms?: SmsEncoding;
        }>(
          `/workspaces/${workspaceId}/campaigns/${campaign.id}/preview-recipients`,
          {
            method: 'POST',
            body: JSON.stringify(body),
          }
        );
        setPreviewCount(response.count);
        setEstimatedCredits(response.estimatedCredits ?? 0);
        setCostBreakdown(response.breakdown ?? null);
        setSmsEncoding(response.sms ?? null);
      } catch (error) {
        // Deliberately silent: this re-runs on every keystroke in the filters, so a toast per
        // failed request would be a stream of them.
        console.warn('[Campaign launch] preview fetch failed:', error);
      } finally {
        setLoadingPreview(false);
      }
    };

    const timer = setTimeout(fetchPreview, 300);
    return () => clearTimeout(timer);
  }, [workspaceId, campaign, mode, selectedIds, filters, channel, customMessage]);

  // Fetch total contacts
  useEffect(() => {
    if (!workspaceId) return;

    const fetchTotal = async () => {
      try {
        const response = await apiFetch<{
          pagination: { total: number };
        }>(`/workspaces/${workspaceId}/contacts?limit=1`);
        setTotalContacts(response.pagination?.total || 0);
      } catch (error) {
        console.warn('[Campaign launch] total contacts fetch failed:', error);
      }
    };

    fetchTotal();
  }, [workspaceId]);

  // Fetch approved WhatsApp templates when channel is whatsapp.
  //
  // There is no `open` in the condition and no replacement for it: the owner of this hook IS the
  // editor, so it is only ever mounted when its content is wanted. The `!open ||` that used to be
  // here was a leftover from the deleted CampaignLaunchDialog - with the prop gone it resolved to the
  // global `window.open`, which is truthy, so the guard was always false and these three effects ran
  // by accident. Declaring a local `const open` anywhere in scope would have silently killed all
  // three. The same note applies to the two effects below.
  //
  // This reads the local mirror, which only a sync refreshes from the provider. The effect after it
  // runs that sync, so a template approved, edited or deleted since anyone last opened the Templates
  // page does not reach this picker stale.
  //
  // Only the latest request may write: the first paint and the post-sync refetch overlap, and an
  // older response landing last would put the stale list back.
  const templatesRequest = useRef(0);
  const templatesLoaded = useRef(false);
  const loadTemplates = useCallback(async ({ silent }: { silent?: boolean } = {}) => {
    const request = ++templatesRequest.current;
    // Silent keeps the Select on screen - a background refresh swapping it for a spinner would
    // yank the picker out from under someone mid-choice.
    if (!silent) {
      setLoadingTemplates(true);
      setTemplatesError(false);
    }
    try {
      const data = await apiFetch<{
        templates: WhatsAppTemplateOption[];
      }>(`/workspaces/${workspaceId}/whatsapp-templates?status=APPROVED&limit=50`);
      if (request !== templatesRequest.current) return;
      templatesLoaded.current = true;
      setWhatsappTemplates(data.templates);
      setTemplatesError(false);
    } catch (error) {
      console.warn('[Campaign launch] failed to fetch WhatsApp templates:', error);
      if (request !== templatesRequest.current) return;
      // A failed refresh leaves the list already on screen alone; it is only an error when there
      // is no list to fall back on.
      if (silent && templatesLoaded.current) return;
      setWhatsappTemplates([]);
      setTemplatesError(true);
    } finally {
      // Cleared by whichever request is latest, silent or not, so a first load superseded by a
      // refresh cannot leave the spinner up.
      if (request === templatesRequest.current) setLoadingTemplates(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId || channel !== 'whatsapp') return;
    loadTemplates();
  }, [workspaceId, channel, loadTemplates]);

  // Pull the current truth from the provider — the same pass the Templates page runs on open. It
  // adds, updates and prunes the mirror, then the list is refetched from it.
  const syncTemplates = useCallback(async ({ silent }: { silent?: boolean } = {}) => {
    setSyncingTemplates(true);
    try {
      const result = await apiFetch<{
        added: number; updated: number; removed: number; total: number;
        error?: string; errorKind?: string;
      }>(`/workspaces/${workspaceId}/whatsapp-templates/sync-all`, { method: 'POST' });

      if (result.error) {
        // A 200 carrying an error: the provider could not be reached and nothing was changed, so
        // the mirror is still usable. Said inline rather than toasted on every open of this step.
        setTemplatesSyncIssue(result.error);
        if (!silent) toast.warning(result.error);
      } else {
        setTemplatesSyncIssue(null);
        if (!silent) {
          const changes = [
            result.added && `${result.added} added`,
            result.updated && `${result.updated} updated`,
            result.removed && `${result.removed} removed`,
          ].filter(Boolean);
          toast.success(
            changes.length
              ? `Synced — ${changes.join(', ')}`
              : `Already up to date (${result.total} template${result.total === 1 ? '' : 's'})`
          );
        }
      }
      await loadTemplates({ silent: true });
      setTemplatesRevision((r) => r + 1);
    } catch (error) {
      if (silent) {
        console.warn('[Campaign launch] background template sync failed:', error);
      } else {
        showError(error, 'Could not sync templates.');
      }
    } finally {
      setSyncingTemplates(false);
    }
  }, [workspaceId, loadTemplates, showError]);

  // Sync once per workspace per mount, the first time the WhatsApp channel is shown. Permissions can
  // arrive after the first render, so the guard is only taken when a sync actually starts; the ref
  // also keeps StrictMode's double-mount in development from calling the provider twice.
  const syncedTemplatesFor = useRef<string | null>(null);
  useEffect(() => {
    if (!workspaceId || channel !== 'whatsapp' || !canSyncTemplates) return;
    if (syncedTemplatesFor.current === workspaceId) return;
    syncedTemplatesFor.current = workspaceId;
    syncTemplates({ silent: true });
  }, [workspaceId, channel, canSyncTemplates, syncTemplates]);

  // A restored selection can point at a template a sync has since pruned or that is no longer
  // approved. It is not cleared - that would silently overwrite the saved config - but it is called
  // out, and nothing is fetched for it.
  const selectedTemplateListed = whatsappTemplates.some((t) => t.id === whatsappTemplateId);
  const selectedTemplateMissing = Boolean(whatsappTemplateId)
    && !selectedTemplateListed && !loadingTemplates && !templatesError && !syncingTemplates;

  // What the selected template needs supplying. Fetched per template rather than read off the list
  // row: the flat columns cannot say whether a header's text holds a {{1}} or a button's URL does,
  // and the server derives that from the provider's own component list.
  useEffect(() => {
    if (!workspaceId || channel !== 'whatsapp' || !whatsappTemplateId || !selectedTemplateListed) {
      setFetchedRequirements(null);
      setLoadingRequirements(false);
      return;
    }

    let cancelled = false;
    setLoadingRequirements(true);

    (async () => {
      try {
        const detail = await apiFetch<{ sendRequirements: SendRequirements }>(
          `/workspaces/${workspaceId}/whatsapp-templates/${whatsappTemplateId}`
        );
        if (!cancelled) setFetchedRequirements({ templateId: whatsappTemplateId, data: detail.sendRequirements });
      } catch (error) {
        if (!cancelled) {
          setFetchedRequirements(null);
          showError(error, 'Could not check what this template needs.');
        }
      } finally {
        if (!cancelled) setLoadingRequirements(false);
      }
    })();

    return () => { cancelled = true; };
    // `templatesRevision`: a sync can change the selected template's header or buttons without its
    // id changing, and these requirements would otherwise describe the template as it was.
  }, [workspaceId, channel, whatsappTemplateId, selectedTemplateListed, showError, templatesRevision]);

  // Only the chosen template's own answer counts (see `fetchedRequirements`). While it loads, the
  // template's list row says what kind of header it has.
  const requirements = fetchedRequirements?.templateId === whatsappTemplateId ? fetchedRequirements.data : null;
  const headerFormat = requirements?.header?.format
    ?? whatsappTemplates.find(t => t.id === whatsappTemplateId)?.headerType
    ?? null;
  const needsMediaHeader = Boolean(headerFormat && MEDIA_HEADER_FORMATS.includes(headerFormat));
  const dynamicButtons = (requirements?.urlButtons || []).filter(b => b.hasVariable);

  const campaignId = campaign?.id ?? null;
  const campaignMediaId = campaign?.campaignMediaId ?? null;
  // Read inside the async fetch below, which must judge its answer against the media the campaign
  // uses NOW, not when the request went out.
  const currentMediaId = useRef(campaignMediaId);
  currentMediaId.current = campaignMediaId;

  // The campaign's own generated media for this header format, and which of it to start with.
  //
  // The default is the SERVER's answer, from the same function a launch falls back to — the media's
  // selected asset, or, for a template made from this very media, the photo it was reviewed with.
  // So what the step preselects and what a launch with no choice would send are one answer.
  //
  // Keyed on plain values rather than the campaign object, so an unrelated refetch (details saved)
  // does not refetch this — and on the template, so picking another template re-defaults the
  // header even when its format is the same. (It used to stay empty in that case.)
  useEffect(() => {
    if (!campaignId || !needsMediaHeader || !headerFormat) {
      setCampaignAssets([]);
      setTemplateDefaultUid(null);
      setLoadingHeaderAssets(false);
      return;
    }

    let cancelled = false;
    const quiet = quietHeaderRefresh.current;
    quietHeaderRefresh.current = false;
    if (!quiet) setLoadingHeaderAssets(true);

    (async () => {
      try {
        const params = new URLSearchParams({ format: headerFormat });
        if (whatsappTemplateId) params.set('templateId', whatsappTemplateId);
        const data = await apiFetch<{
          assets: CampaignAsset[];
          defaultUid: string | null;
          templateDefaultUid?: string | null;
          campaignMediaId?: string | null;
        }>(`/workspaces/${workspaceId}/campaigns/${campaignId}/header-media?${params.toString()}`);
        // An answer about media the campaign no longer uses is dropped. Uids like gen-1-photo-1
        // repeat across media items, so applying it would preselect a different picture under the
        // same name.
        if (cancelled || (data.campaignMediaId ?? null) !== currentMediaId.current) return;
        setCampaignAssets(data.assets);
        setTemplateDefaultUid(data.templateDefaultUid ?? null);
        // A choice that still names one of these assets is kept — somebody made it. Anything else
        // (nothing yet, or a uid that no longer exists) becomes what the server would send.
        setMediaChoice((prev) => (prev === CUSTOM_MEDIA || data.assets.some((asset) => asset.uid === prev)
          ? prev
          : (data.defaultUid || '')));
      } catch (error) {
        console.warn('[Campaign launch] header media fetch failed:', error);
        if (!cancelled && !quiet) {
          setCampaignAssets([]);
          setTemplateDefaultUid(null);
        }
      } finally {
        if (!cancelled) setLoadingHeaderAssets(false);
      }
    })();

    return () => { cancelled = true; };
  }, [workspaceId, campaignId, campaignMediaId, needsMediaHeader, headerFormat, whatsappTemplateId, headerAssetsRefresh]);

  // Choosing different creative on the media step: an asset uid belongs to the media it came from,
  // so it is dropped and the header-media fetch above preselects the new media's default instead.
  // An upload or a pasted link is not tied to the media, so it is kept.
  const mediaIdSeen = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (mediaIdSeen.current !== undefined && mediaIdSeen.current !== campaignMediaId) {
      setMediaChoice((prev) => (prev && prev !== CUSTOM_MEDIA ? '' : prev));
    }
    mediaIdSeen.current = campaignMediaId;
  }, [campaignMediaId]);

  const handleMediaUpload = async (file: File | undefined) => {
    if (!file || !whatsappTemplateId) return;

    setUploadingMedia(true);
    try {
      const form = new FormData();
      form.append('file', file);

      // The template-scoped endpoint, because the upload has to be validated against the header
      // format the template was actually approved with.
      const result = await apiFetch<{ mediaId: string }>(
        `/workspaces/${workspaceId}/whatsapp-templates/${whatsappTemplateId}/media`,
        { method: 'POST', body: form }
      );

      setHeaderMediaId(result.mediaId);
      setUploadedMediaName(file.name);
      // A file and a URL are alternatives; keeping both would only raise the question of which won.
      setHeaderMediaUrl('');
      toast.success(`${file.name} uploaded`);
    } catch (error) {
      showError(error, 'Could not upload that file.');
    } finally {
      setUploadingMedia(false);
    }
  };

  // Point the campaign at different creative: PATCH, then refetch (the response carries the media's
  // re-signed asset URLs, which a locally merged object would not have).
  //
  // ONE AT A TIME. A template choice can switch the media while a card click on the media step
  // switches it again; run side by side, the two could land in either order and leave the server
  // holding one media while the screen shows the other. So each switch waits for the one before,
  // and only the LATEST one's refetch is applied. `switchingMedia` holds Next and Launch meanwhile,
  // and a launch waits for the chain as well as for the autosave.
  const [switchingMedia, setSwitchingMedia] = useState(false);
  const mediaSwitchSeq = useRef(0);
  const mediaSwitchChain = useRef<Promise<unknown>>(Promise.resolve());

  const switchCampaignMedia = (mediaId: string): Promise<boolean> => {
    if (!campaign || !onCampaignChange) return Promise.resolve(false);
    const seq = ++mediaSwitchSeq.current;
    const id = campaign.id;
    setSwitchingMedia(true);

    const run = mediaSwitchChain.current.then(async () => {
      try {
        await apiFetch(`/workspaces/${workspaceId}/campaigns/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ campaignMediaId: mediaId }),
        });
        if (seq !== mediaSwitchSeq.current) return true;
        const data = await apiFetch<{ campaign: LaunchableCampaign }>(`/workspaces/${workspaceId}/campaigns/${id}`);
        if (seq === mediaSwitchSeq.current) onCampaignChange(data.campaign);
        return true;
      } catch (error) {
        if (seq === mediaSwitchSeq.current) showError(error, 'Could not change the media.');
        return false;
      } finally {
        if (seq === mediaSwitchSeq.current) setSwitchingMedia(false);
      }
    });
    mediaSwitchChain.current = run;
    return run;
  };

  // Changing the template invalidates everything gathered for the previous one — a media id
  // uploaded against an IMAGE header is not an answer for a VIDEO one.
  //
  // A handler rather than an effect on `whatsappTemplateId`. As an effect it also fired on mount and
  // again when hydration set the restored id, wiping the restored header media, header text and
  // button values every time a draft was reopened. Only the operator picking a template is a change.
  //
  // A template made from generated media brings that media as the campaign's creative — the
  // default at the media step, with the photo it was reviewed with as the header. Applied only when
  // the campaign has no media yet, or still has the PREVIOUS template's default: media somebody
  // picked for themselves is never replaced. The media step offers the template's instead.
  const chooseTemplate = (id: string) => {
    if (id !== whatsappTemplateId) {
      setMediaChoice('');
      setHeaderMediaUrl('');
      setHeaderMediaId('');
      setUploadedMediaName('');
      setHeaderText('');
      setButtonValues({});

      const next = whatsappTemplates.find((t) => t.id === id);
      const previous = whatsappTemplates.find((t) => t.id === whatsappTemplateId);
      const current = campaign?.campaignMediaId ?? null;
      if (
        next?.campaignMediaId
        && next.campaignMediaId !== current
        && (!current || current === previous?.campaignMediaId)
      ) {
        switchCampaignMedia(next.campaignMediaId);
      }
    }
    setWhatsappTemplateId(id);
  };

  // Picking a media asset from the header Select. Choosing a campaign asset discards an upload or a
  // pasted link, since only one of them can be the header.
  const chooseHeaderMedia = (value: string) => {
    setMediaChoice(value);
    if (value !== CUSTOM_MEDIA) { setHeaderMediaId(''); setUploadedMediaName(''); setHeaderMediaUrl(''); }
  };

  const chosenAsset = campaignAssets.find((a) => a.uid === mediaChoice) || null;

  // Whether the header has something to send. Mirrors the server's own check, which accepts an
  // uploaded id, a pasted URL, or one of the campaign's own assets — and refuses the launch
  // otherwise, before reserving a single credit.
  //
  // An asset counts only when it is actually among the media's assets, not merely because a uid is
  // held: a saved uid whose generation was discarded used to show an empty picker while Launch
  // stayed enabled, for a launch the server then refused.
  const hasHeaderMedia = !needsMediaHeader
    || Boolean(headerMediaId)
    || (mediaChoice === CUSTOM_MEDIA ? Boolean(headerMediaUrl.trim()) : Boolean(chosenAsset));

  // Always the backend's count. It excludes contacts without a phone number — which is what
  // actually gets sent to and charged for — whereas `selectedIds.length` counts every ticked
  // row, so the old version overstated the credits in the confirmation dialog.
  const recipientCount = previewCount;

  // Only the kinds that actually have recipients, so an SMS send doesn't list WhatsApp rows.
  const costLines = (Object.entries(costBreakdown || {}) as [CostKind, CostLine][])
    .filter(([, line]) => line.count > 0);

  // How many ticked contacts will be skipped, so the gap is explained rather than surprising.
  const skippedNoPhone =
    mode === 'selected' ? Math.max(0, selectedIds.length - previewCount) : 0;
  const defaultMessage =
    campaign?.media?.generatedContent?.text || '';

  const selectedTemplate = whatsappTemplates.find((t) => t.id === whatsappTemplateId) || null;

  const isWhatsApp = channel === 'whatsapp';
  const hasMedia = Boolean(campaign?.campaignMediaId);

  // The media the chosen template was made from: already the campaign's creative (said on the
  // media step, so the preselection is not a mystery), or on offer when somebody picked other media.
  const templateMediaId = isWhatsApp ? (selectedTemplate?.campaignMediaId ?? null) : null;
  const templateMediaApplied = Boolean(templateMediaId) && templateMediaId === campaignMediaId;
  const templateMediaSuggestion = templateMediaId && !templateMediaApplied ? templateMediaId : null;

  // Everything that keeps the Launch button disabled, apart from a launch already in flight, grouped
  // by the step that fixes it. The channel and media steps use their own group to lock Next; the
  // final preview lists all of them, each linking to its step, so a disabled Launch always says why.
  const templateBlockers: string[] = [];
  if (isWhatsApp) {
    const unmapped = unmappedVariablesOf(selectedTemplate);
    // Not listed = pruned by a sync or no longer approved; the picker shows why.
    if (!whatsappTemplateId) templateBlockers.push('Choose a WhatsApp template.');
    else if (!selectedTemplateListed) {
      templateBlockers.push(loadingTemplates || syncingTemplates
        ? 'Loading templates…'
        : 'The selected template is no longer approved. Choose another.');
    }
    // Both mirror the server-side guards in launchCampaign, so the button is not offered for a send
    // the server will refuse. The first is a certain, loud failure — every message rejected for a
    // parameter-count mismatch. The second is a suspected, silent one: the provider rewrote the body
    // under a saved mapping, so the messages would go out accepted and delivered, carrying the wrong
    // personal detail.
    else if (unmapped.length > 0) {
      templateBlockers.push(`${unmapped.length} template variable${unmapped.length === 1 ? ' is' : 's are'} not mapped to a contact field.`);
    } else if (needsMappingReview(selectedTemplate)) {
      templateBlockers.push("The template's variable mapping needs confirming.");
    }
    // The rest of what the server now refuses, mirrored so the refusal is visible before it is spent
    // rather than after. A carousel cannot be sent at all; a media header, a variable text header and
    // a dynamic button each need a value, and without them Meta rejects every message in the
    // campaign for a component mismatch.
    if (whatsappTemplateId && loadingRequirements) templateBlockers.push('Checking what this template needs…');
    if (requirements?.hasCarousel) templateBlockers.push('Carousel templates are not supported yet.');
    if (requirements?.header?.hasVariable && !headerText.trim()) templateBlockers.push('Fill in the header value.');
    for (const button of dynamicButtons) {
      if (!(buttonValues[String(button.index)] || '').trim()) {
        templateBlockers.push(`Fill in the link value for the "${button.text || 'URL'}" button.`);
      }
    }
  }

  const mediaBlockers: string[] = [];
  if (switchingMedia) mediaBlockers.push('Saving the media choice…');
  else if (!hasMedia) mediaBlockers.push('Choose the media to send.');
  else if (isWhatsApp && !hasHeaderMedia) {
    mediaBlockers.push(loadingHeaderAssets
      ? 'Loading the header media…'
      : `Choose the header ${headerFormat ? headerFormat.toLowerCase() : 'media'} for the template.`);
  }

  const audienceBlockers: string[] = recipientCount === 0
    ? ['Select at least one contact with a phone number.']
    : [];

  const launchBlockers: { step: 'channel' | 'media' | 'audience'; message: string }[] = [
    ...templateBlockers.map((message) => ({ step: 'channel' as const, message })),
    ...mediaBlockers.map((message) => ({ step: 'media' as const, message })),
    ...audienceBlockers.map((message) => ({ step: 'audience' as const, message })),
  ];
  const launchBlocked = launchBlockers.length > 0;

  const handleLaunch = async () => {
    // `launching` is the double-click guard: the flush below is awaited, so a second click can
    // arrive before the button re-renders disabled. The server refuses a second launch of a
    // campaign that is already sending, but the operator should never get to see that error.
    if (!campaign || previewCount === 0 || launching) return;

    setLaunching(true);
    // A media switch still being written goes first: the launch reads the campaign's media on the
    // server, and must read the one on screen.
    await mediaSwitchChain.current;
    // Write any pending autosave BEFORE launching. The server also refuses a PATCH to a launched
    // campaign, which is the real guarantee — this just avoids provoking it in the normal case.
    await flushConfig();
    try {
      const body: Record<string, unknown> = { mode, channel };

      if (mode === 'selected') {
        body.contactIds = selectedIds;
      } else if (mode === 'filtered') {
        body.filters = filters;
      }

      if (scheduled && scheduleDate && scheduleTime) {
        body.scheduleAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      }

      if (channel === 'whatsapp' && whatsappTemplateId) {
        body.whatsappTemplateId = whatsappTemplateId;

        // One value for the whole campaign, unlike the body variables which are per contact: a
        // header image, a header's {{1}} and a dynamic button's link are properties of the message,
        // not of who receives it.
        if (needsMediaHeader) {
          if (headerMediaId) body.headerMedia = { mediaId: headerMediaId };
          else if (mediaChoice === CUSTOM_MEDIA && headerMediaUrl) body.headerMedia = { url: headerMediaUrl };
          else if (mediaChoice && mediaChoice !== CUSTOM_MEDIA) body.headerMedia = { assetUid: mediaChoice };
        }
        if (requirements?.header?.hasVariable && headerText) body.headerText = headerText;
        if (dynamicButtons.length > 0) body.buttonValues = buttonValues;
      }

      if (channel !== 'whatsapp' && customMessage.trim()) {
        body.message = customMessage;
      }

      const response = await apiFetch<{
        status: string;
        recipientCount: number;
        creditsUsed?: number;
        channel?: string;
        warning?: string;
      }>(`/workspaces/${workspaceId}/campaigns/${campaign.id}/launch`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      // Raised when the media had to be sent as a link because it was too large to upload — the
      // less reliable path, and worth knowing about before wondering why some did not arrive.
      if (response.warning) toast.warning(response.warning);

      const channelLabel = response.channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
      if (response.status === 'scheduled') {
        toast.success(`Campaign scheduled for ${response.recipientCount} recipients via ${channelLabel}`);
      } else {
        toast.success(`Campaign launched to ${response.recipientCount} recipients via ${channelLabel}`);
      }

      // No navigation: the page it lives on swaps from editor to results in place.
      // 'running' rather than 'live': both are in LAUNCHED_STATUSES so the page swaps to results
      // either way, but the server writes 'running' and the badge would otherwise read "live" until
      // the next reload changed it under the user.
      onLaunched({ id: campaign.id, status: response.status === 'scheduled' ? 'scheduled' : 'running' });

    } catch (error) {
      console.error('Failed to launch campaign:', error);
      showError(error, 'Failed to launch this campaign.');
    } finally {
      setLaunching(false);
      // The launch reserved credits; everything showing the balance should say so now.
      refreshWorkspace();
    }
  };

  return {
    workspaceId,
    canSyncTemplates,

    // Audience
    mode, setMode,
    selectedIds, setSelectedIds,
    filters, setFilters,
    totalContacts,
    recipientCount,
    loadingPreview,
    skippedNoPhone,
    audienceBlockers,
    audienceValid: audienceBlockers.length === 0,

    // Channel
    channel,
    templateBlockers,
    channelValid: templateBlockers.length === 0,

    // Media
    hasMedia,
    mediaBlockers,
    mediaValid: mediaBlockers.length === 0,
    switchCampaignMedia,
    switchingMedia,
    // The chosen template's own media: whether the campaign is using it, or it is on offer.
    templateMediaId,
    templateMediaApplied,
    templateMediaSuggestion,

    // Cost
    estimatedCredits,
    costLines,
    smsEncoding,

    // Template
    whatsappTemplates,
    whatsappTemplateId,
    chooseTemplate,
    selectedTemplate,
    selectedTemplateListed,
    selectedTemplateMissing,
    loadingTemplates,
    templatesError,
    syncingTemplates,
    templatesSyncIssue,
    syncTemplates,
    requirements,
    loadingRequirements,
    headerFormat,
    needsMediaHeader,
    dynamicButtons,

    // Header and buttons
    campaignAssets,
    loadingHeaderAssets,
    /** A header preview failed to load (its signed URL expired): fetch fresh ones, quietly. */
    refreshHeaderPreviews,
    templateDefaultUid,
    chosenAsset,
    mediaChoice,
    chooseHeaderMedia,
    headerMediaUrl, setHeaderMediaUrl,
    headerMediaId,
    uploadedMediaName,
    uploadingMedia,
    handleMediaUpload,
    headerText, setHeaderText,
    buttonValues, setButtonValues,

    // SMS
    customMessage, setCustomMessage,
    defaultMessage,

    // Schedule (disabled — see FinalPreviewStep)
    scheduled,
    scheduleDate, setScheduleDate,
    scheduleTime, setScheduleTime,

    // Preview
    previewRecipients,
    previewRecipientId, setPreviewRecipientId,
    previewRecipient,

    // Launch
    launching,
    launchBlockers,
    launchBlocked,
    handleLaunch,

    /** Write any pending autosave now. Called on every step change, so a reload loses nothing. */
    flush: flushConfig,
  };
}

export type CampaignLaunchState = ReturnType<typeof useCampaignLaunchState>;

export default useCampaignLaunchState;
